import type {
  CommsAddressDetail,
  CommsBrand,
  CommsChannel,
  CommsPurpose,
  CommsSettings,
  ConsentImportScope,
  ConsentImportStatus,
  ConsentStatement,
  PersonCommsPanel,
  RecordConsentBody,
  SuppressionChangeBody,
  SuppressionReason,
} from '@bre-hub/contracts'
import { request } from './api'

// Communications (ADR 0034): the staff side of the consent ledger.

export type CommsConfig = {
  brands: CommsBrand[]
  purposes: CommsPurpose[]
  statements: ConsentStatement[]
  settings: CommsSettings
  importScopes: ConsentImportScope[]
  can: { manage: boolean; consent: boolean; suppression: boolean; links: boolean; people: boolean }
  switches: { consentImport: boolean; consentRead: boolean; formsOrigin: string | null }
}

export type SuppressionListRow = { addressId: string; kind: 'email' | 'phone'; value: string; channel: CommsChannel; scope: string | null; scopeName: string | null; reason: SuppressionReason; since: string; decidingEventId: string; source: string; note: string | null }

export type ImportsView = {
  on: boolean
  apps: (ConsentImportStatus & { notConnected: string | null })[]
  unmapped: { app: string; scope: string; n: number }[]
  forms: { imported: number; waiting: number }
}

export const commsApi = {
  config: () => request<CommsConfig>('GET', '/api/comms/config'),
  lookup: (kind: 'email' | 'phone', value: string) =>
    request<{ found: boolean; normalized: string | null; detail?: CommsAddressDetail }>('GET', `/api/comms/addresses/lookup?kind=${kind}&value=${encodeURIComponent(value)}`),
  address: (id: string) => request<CommsAddressDetail>('GET', `/api/comms/addresses/${encodeURIComponent(id)}`),
  recordConsent: (body: RecordConsentBody) => request<{ eventId: string; detail: CommsAddressDetail }>('POST', '/api/comms/consent', body),
  changeSuppression: (body: SuppressionChangeBody) => request<{ eventId: string; detail: CommsAddressDetail }>('POST', '/api/comms/suppressions', body),
  suppressions: (params: { channel?: string; reason?: string; before?: string }) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => !!v) as [string, string][])
    return request<{ suppressions: SuppressionListRow[]; nextBefore: string | null; totals: Record<string, number> }>('GET', `/api/comms/suppressions${q.size ? `?${q}` : ''}`)
  },
  personPanel: (personId: string) => request<PersonCommsPanel>('GET', `/api/people/${encodeURIComponent(personId)}/comms`),
  makeLink: (addressId: string, channel: CommsChannel, brandKey: string | null) =>
    request<{ expiresAt: string; preferenceUrl: string | null; unsubscribeUrl: string | null; reason: string | null }>('POST', `/api/comms/addresses/${encodeURIComponent(addressId)}/links`, { channel, brandKey }),
  imports: () => request<ImportsView>('GET', '/api/comms/imports'),
  runImport: (full: boolean) => request<{ status: string; message: string | null; apps: { app: string; status: string; pages: number; counts: Record<string, number>; message: string | null }[] }>('POST', '/api/comms/imports/run', { full }),
  saveBrand: (key: string, body: Omit<CommsBrand, 'key' | 'updatedAt' | 'updatedByName'>) => request('PUT', `/api/comms/brands/${encodeURIComponent(key)}`, body),
  addBrand: (body: Omit<CommsBrand, 'updatedAt' | 'updatedByName'>) => request('POST', '/api/comms/brands', body),
  savePurpose: (key: string, body: { name: string; description: string; active: boolean }) => request('PUT', `/api/comms/purposes/${encodeURIComponent(key)}`, body),
  addStatement: (body: { brandKey: string; purposeKey: string; channel: CommsChannel; text: string }) => request('POST', '/api/comms/statements', body),
  editStatement: (id: string, text: string) => request('PUT', `/api/comms/statements/${encodeURIComponent(id)}`, { text }),
  retireStatement: (id: string) => request('POST', `/api/comms/statements/${encodeURIComponent(id)}/retire`),
  saveSettings: (body: { doubleOptInPublicForms: boolean; preferenceLinkDays: number; formConsentBrandKey: string }) => request('PUT', '/api/comms/settings', body),
  saveScope: (body: ConsentImportScope) => request('PUT', '/api/comms/import-scopes', body),
  rebuild: (offset: number) => request<{ consentKeys: number; suppressionKeys: number; nextOffset: number | null }>('POST', '/api/comms/rebuild', { offset }),
}
